import React from 'react';
import { DecoratorNode, type LexicalNode, type NodeKey } from 'lexical';

import ResizableImage from './ResizableImage';

type ImagePayload = {
    src: string; // local://GUID or final URL
    alt?: string;
    width?: number;
    height?: number;
};

export class ImageNode extends DecoratorNode<React.ReactNode> {
    __src: string;
    __alt: string;
    __width: number;
    __height: number;
    __alignment: string | null = null; // Add alignment property

    static getType(): string {
        return 'image';
    }
    static clone(node: ImageNode): ImageNode {
        return new ImageNode(
            {
                src: node.__src,
                alt: node.__alt,
                width: node.__width,
                height: node.__height,
            },
            node.__key
        );
    }

    constructor({ src, alt = '', width = 300, height = 300 }: ImagePayload, key?: NodeKey) {
        super(key);
        this.__src = src;
        this.__alt = alt;
        this.__width = width;
        this.__height = height;
    }

    exportJSON() {
        return {
            type: 'image',
            version: 1,
            src: this.__src,
            alt: this.__alt,
            width: this.__width,
            height: this.__height,
        };
    }

    static importJSON(json: any): ImageNode {
        return new ImageNode({
            src: json.src,
            alt: json.alt,
            width: json.width,
            height: json.height,
        });
    }

    setDimensions(width: number, height: number) {
        const writable = this.getWritable();
        writable.__width = width;
        writable.__height = height;
    }

    setFormat(alignment: string) {
        const writable = this.getWritable();
        writable.__alignment = alignment; // Set alignment
    }

    createDOM(): HTMLElement {
        const dom = document.createElement('span');
        dom.setAttribute('contenteditable', 'false'); // Prevent editing inside the image node

        if (this.__alignment) {
            dom.style.textAlign = this.__alignment; // Apply alignment style
        }

        dom.addEventListener('mousedown', (event) => {
            event.preventDefault(); // Prevent default behavior to avoid conflicts
            const selection = window.getSelection();
            if (selection) {
                const range = document.createRange();
                range.selectNode(dom);
                selection.removeAllRanges();
                selection.addRange(range); // Select the entire image node
            }
        });

        return dom;
    }
    updateDOM(_prevNode: ImageNode): boolean {
        return false;
    }

    decorate(): React.JSX.Element {
        return (
            <ResizableImage
                nodeKey={this.getKey()}
                src={this.__src}
                alt={this.__alt}
                width={this.__width}
                height={this.__height}
            />
        );
    }
}

export function $createImageNode(payload: ImagePayload): ImageNode {
    return new ImageNode(payload);
}
export function $isImageNode(node?: LexicalNode): node is ImageNode {
    return node instanceof ImageNode;
}
